import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import SideBar from "../dashboard admin/components/SideBar";
import api from "../../../services/api/api";
import Swal from "sweetalert2";

const CreatePublication = () => {
  const [files, setFiles] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);

  const getAuthToken = () => localStorage.getItem("token");

  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      api
        .get("api/categories", { headers: { Authorization: `Bearer ${token}` } })
        .then((response) => setCategories(response.data || []))
        .catch((error) => {
          console.error("Erro ao buscar categorias:", error);
          Swal.fire({
            icon: "error",
            title: "Erro",
            text: "Erro ao carregar categorias. Tente novamente.",
            confirmButtonColor: "#1cb5e0",
          });
        });
    } else {
      console.error("Token de autenticação não encontrado.");
      Swal.fire({
        icon: "error",
        title: "Erro de Autenticação",
        text: "Token de autenticação não encontrado. Faça login novamente.",
        confirmButtonColor: "#1cb5e0",
      });
    }
  }, []);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files).slice(0, 20);
    const newDocuments = selectedFiles.map((file) => ({
      file,
      title: file.name.replace(/\.pdf$/, ""),
      category: "",
      publicationDate: "",
      isPublished: false,
    }));
    setDocuments(newDocuments);

    if (selectedFiles.length > 0) {
      Swal.fire({
        icon: "success",
        title: "Arquivos Carregados",
        text: `${selectedFiles.length} arquivo(s) PDF carregado(s) com sucesso!`,
        timer: 2000,
        showConfirmButton: false,
      });
    }
  };

  const handleDocumentChange = (index, field, value) => {
    const updatedDocuments = [...documents];
    updatedDocuments[index][field] = value;
    setDocuments(updatedDocuments);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = getAuthToken();
    
    if (!token) {
      console.error("Token de autenticação não encontrado.");
      Swal.fire({
        icon: "error",
        title: "Erro de Autenticação",
        text: "Token de autenticação não encontrado. Faça login novamente.",
        confirmButtonColor: "#1cb5e0",
      });
      return;
    }

    // Validação básica
    const invalidDocs = documents.filter(doc => !doc.title || !doc.category || !doc.publicationDate);
    if (invalidDocs.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Campos Obrigatórios",
        text: "Por favor, preencha todos os campos obrigatórios (título, categoria e data de publicação).",
        confirmButtonColor: "#1cb5e0",
      });
      return;
    }

    // Confirmação antes de enviar
    const result = await Swal.fire({
      title: "Confirmar Criação",
      text: `Deseja criar ${documents.length} publicação(ões)?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#1cb5e0",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sim, criar!",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    // Loading durante o upload
    Swal.fire({
      title: "Criando Publicações...",
      text: "Por favor, aguarde enquanto processamos seus arquivos.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    for (const [index, doc] of documents.entries()) {
      const formData = new FormData();
      formData.append("file", doc.file);
      formData.append("name", doc.title);
      formData.append("category", doc.category);
      formData.append("publicationDate", doc.publicationDate);
      formData.append("isPublished", doc.isPublished);

      try {
        await api.post("api/publications", formData, {
          headers: { 
            Authorization: `Bearer ${token}`, 
            "Content-Type": "multipart/form-data" 
          },
        });
        successCount++;
      } catch (error) {
        console.error("Erro ao criar publicação:", error);
        errorCount++;
        errors.push(`${doc.title}: ${error.response?.data?.message || error.message}`);
      }
    }

    // Fechar loading
    Swal.close();

    // Mostrar resultado final
    if (errorCount === 0) {
      Swal.fire({
        icon: "success",
        title: "Sucesso!",
        text: `${successCount} publicação(ões) criada(s) com sucesso!`,
        confirmButtonColor: "#1cb5e0",
      }).then(() => {
        setDocuments([]);
      });
    } else if (successCount === 0) {
      Swal.fire({
        icon: "error",
        title: "Erro",
        html: `Não foi possível criar nenhuma publicação.<br><br><strong>Erros:</strong><br>${errors.join('<br>')}`,
        confirmButtonColor: "#1cb5e0",
      });
    } else {
      Swal.fire({
        icon: "warning",
        title: "Parcialmente Concluído",
        html: `${successCount} publicação(ões) criada(s) com sucesso.<br>${errorCount} falharam.<br><br><strong>Erros:</strong><br>${errors.join('<br>')}`,
        confirmButtonColor: "#1cb5e0",
      }).then(() => {
        setDocuments([]);
      });
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      <Box>
        <SideBar />
      </Box>
      <Box sx={{ flex: 1, p: 3 }}>
        <Typography variant="h4" fontWeight="bold" mb={3}>
          Criar nova publicação
        </Typography>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ backgroundColor: "#fff", p: 3, borderRadius: 2, boxShadow: 3, maxWidth: "600px", mx: "auto" }}
        >
          <Button
            variant="contained"
            component="label"
            fullWidth
            sx={{ mb: 2, textTransform: "none", background: "linear-gradient(90deg, #1cb5e0, #000851)" }}
          >
            Enviar PDFs (Máx. 20 PDF)
            <input type="file" hidden accept="application/pdf" multiple onChange={handleFileChange} />
          </Button>

          {documents.map((doc, index) => (
            <Box key={index} sx={{ mb: 2, p: 2, border: "1px solid #ccc", borderRadius: 2 }}>
              <TextField
                label="Nome da publicação"
                fullWidth
                variant="outlined"
                value={doc.title}
                onChange={(e) => handleDocumentChange(index, "title", e.target.value)}
                sx={{ mb: 1 }}
                required
              />

              <TextField
                select
                label="Categoria"
                fullWidth
                variant="outlined"
                value={doc.category}
                onChange={(e) => handleDocumentChange(index, "category", e.target.value)}
                sx={{ mb: 1 }}
                required
              >
                {categories.map((cat) => (
                  <MenuItem key={cat._id} value={cat._id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Data da publicação"
                type="date"
                fullWidth
                variant="outlined"
                value={doc.publicationDate}
                onChange={(e) => handleDocumentChange(index, "publicationDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 1 }}
                required
              />

              <FormControlLabel
                control={<Checkbox checked={doc.isPublished} onChange={(e) => handleDocumentChange(index, "isPublished", e.target.checked)} color="primary" />}
                label="Publicado"
                sx={{ mb: 1 }}
              />

              <Button
                variant="outlined"
                color="primary"
                fullWidth
                onClick={() => window.open(URL.createObjectURL(doc.file), "_blank")}
                sx={{ mb: 1 }}
              >
                Visualizar PDF
              </Button>
            </Box>
          ))}

          {documents.length > 0 && (
            <Button type="submit" variant="contained" fullWidth sx={{ textTransform: "none", background: "linear-gradient(90deg, #1cb5e0, #000851)" }}>
              Criar publicação
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CreatePublication;